import type { AttachmentItem } from '@usealloy/component-library';
import {
  Avatar,
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  CommentBox,
  CommentBoxActions,
  CommentBoxAttachments,
  IconName,
  Modal,
  Text,
  Tooltip,
  AvatarRole,
  AvatarSize,
  Icon,
  Separator,
} from '@usealloy/component-library';
import { StyledDownloadLink } from '~/investigations/InvestigationView/styles';
import {
  useContext,
  useRef,
  useState,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserContext } from '~/dashboard/context';
import { typhonRoutes } from '~/helpers/routes';
import { useEvidencePermissions } from '~/helpers/hooks';
import { typhonAjaxFetch, typhonMultipartFormDataRequest } from '~/helpers/api';
import { EVIDENCE_DATA_TYPES } from '~/constants';
import { getEvidenceActionDescription } from '~/helpers/evidence';
import { useDocumentDownload } from '~/hooks/useDocumentDownload';
import { useAppDispatch } from '~/store/reduxHooks';
import { loadInvestigationEvidence } from '~/investigations/redux/thunks';
import { generateAudit } from '~/dashboard/helpers';

// Evidence types
interface EvidenceNote {
  note_id: string;
  text: string;
}

interface EvidenceDocument {
  document_token: string;
  name: string;
  extension: string;
  size_bytes?: number;
  sizeBytes?: number;
}

interface EvidenceEvent {
  evidence_event_token: string;
  created_at: string;
  created_by: {
    agent_id: number;
    email: string;
    first_name: string;
    last_name: string;
    token: string;
  } | null;
  notes: EvidenceNote[];
  documents: EvidenceDocument[];
}

interface EvidenceData {
  events: EvidenceEvent[];
}

// Move components outside of main component to avoid re-creation on every render
const AttachButton = ({ onClick }: { onClick: () => void }) => (
  <Box>
    <Tooltip content="Attach">
      <Button
        icon={IconName.Paperclip}
        variant={ButtonVariant.tertiary}
        onClick={onClick}
        size={ButtonSize.small}
      />
    </Tooltip>
    <Text
      size={1}
      lineHeight={1}
      style={{
        color: 'var(--cl-text-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      You can upload up to 10 files (50MB total) in JPG, PNG, PDF, HEIF, HEIC, or MSG format.
    </Text>
  </Box>
);

const SubmitButton = ({
  onClick,
  disabled,
  loading,
}: {
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
}) => (
  <Tooltip content="Submit">
    <Button
      icon={!loading ? IconName.Send : undefined}
      variant={ButtonVariant.primary}
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      size={ButtonSize.small}
    />
  </Tooltip>
);

export type AddEvidenceModalProps = {
  closeModal: () => void;
  isOpen: boolean;
  sourceTokens: string[];
  sourceType: typeof EVIDENCE_DATA_TYPES.TRANSACTION | typeof EVIDENCE_DATA_TYPES.BANK_ACCOUNT;
  contextToken?: string;
  sourceComponent: React.ReactNode;
};

export const EvidenceModal = ({
  isOpen,
  closeModal,
  sourceTokens,
  sourceType,
  contextToken,
  sourceComponent,
}: AddEvidenceModalProps) => {
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const { typhonDomain, permissions } = useContext(UserContext);
  const [error, setError] = useState<string | null>(null);

  // Check permissions
  const { canReadEvidence, canCreateEvidence } = useEvidencePermissions();
  const canDownloadDocuments = !!permissions?.['READ:Documents:null'];
  const [totalBytes, setTotalBytes] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const MAX_TOTAL_BYTES = 50 * 1024 * 1024; // 50 MB across all files
  const { downloadDocuments } = useDocumentDownload();
  const MAX_DOCUMENTS = 10;
  const ALLOWED_FILE_TYPES = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'image/heif',
    'image/heic',
    'application/vnd.ms-outlook',
    'application/x-cfb', // MSG files (Compound File Binary Format)
  ];
  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heif', '.heic', '.pdf', '.msg'];
  const queryClient = useQueryClient();
  const appDispatch = useAppDispatch();

  // Derive attachments from files for UI display
  const attachments: AttachmentItem[] = files.map(file => ({
    id: `${file.name}-${file.size}-${file.lastModified}`,
    label: file.name,
    icon: IconName.Files,
  }));

  // Fetch evidence for all selected source tokens in a single request
  // Only fetch if user can read evidence
  const { data: evidenceData } = useQuery({
    queryKey: ['evidence', contextToken, ...sourceTokens.sort()],
    queryFn: async (): Promise<EvidenceData> => {
      if (sourceTokens.length === 0) {
        return { events: [] };
      }

      const url = `${typhonDomain}${typhonRoutes.evidence.fetch({ sourceTokens })}`;
      const response = await typhonAjaxFetch(url);
      return response as EvidenceData;
    },
    enabled: isOpen && sourceTokens.length > 0 && canReadEvidence,
    initialData: { events: [] } as EvidenceData,
    onError: () => {
      setError(`Error fetching ${sourceType} evidence`);
    },
  });

  const addEvidenceMutation = useMutation({
    mutationFn: async (payload: {
      evidence_sources: {
        evidence_source_token: string;
        evidence_source_type: string;
      }[];
      notes?: { text: string }[];
      documents?: File[];
      context_token: string;
    }) => {
      const url = `${typhonDomain}${typhonRoutes.evidence.upload}`;
      const formData = new FormData();

      const evidenceSourcesJson = JSON.stringify(payload.evidence_sources);
      formData.append('evidence_sources', evidenceSourcesJson);

      if (payload.notes && payload.notes.length > 0) {
        const notesJson = JSON.stringify(payload.notes);
        formData.append('notes', notesJson);
      }
      formData.append('context_token', payload.context_token);

      if (payload.documents && payload.documents.length > 0) {
        payload.documents.forEach(doc => {
          formData.append('documents', doc);
        });
      }

      const response = await typhonMultipartFormDataRequest(url, {
        method: 'POST',
        body: formData,
      });
      return response;
    },
    onSuccess: async (_, variables) => {
      // Invalidate queries for all source tokens
      sourceTokens.forEach(token => {
        queryClient.invalidateQueries({ queryKey: ['evidence', contextToken, token] });
      });

      // Reload investigation evidence to update timeline
      if (contextToken) {
        await appDispatch(loadInvestigationEvidence({
          investigationToken: contextToken,
          typhonDomain,
        }));
      }

      const hasNote = !!variables?.notes?.length;
      const hasDocuments = !!variables?.documents?.length;
      const documentCount = variables.documents?.length || 0;
      const sourceCount = sourceTokens.length;
      const isTransaction = sourceType === EVIDENCE_DATA_TYPES.TRANSACTION;
      const isBankAccount = sourceType === EVIDENCE_DATA_TYPES.BANK_ACCOUNT;

      let auditMessage = 'Added ';

      // Determine what was added
      if (hasNote && hasDocuments) {
        const docText = documentCount === 1 ? 'a document' : 'documents';
        auditMessage += `a note and ${docText} to `;
      } else if (hasNote) {
        auditMessage += 'a note to ';
      } else if (hasDocuments) {
        const docText = documentCount === 1 ? 'a document' : 'documents';
        auditMessage += `${docText} to `;
      }

      // Determine what it was added to
      if (isTransaction) {
        const trxText = sourceCount === 1 ? 'a transaction' : 'transactions';
        auditMessage += trxText;
      } else if (isBankAccount) {
        auditMessage += 'a bank account';
      }

      const auditDetails: Record<string, any> = {
        context_token: contextToken,
      };

      if (isTransaction) {
        // Create a numbered list for transaction tokens
        auditDetails['Transaction Tokens'] = sourceTokens.reduce((acc, token, index) => {
          acc[`${index + 1}`] = token;
          return acc;
        }, {} as Record<string, string>);
      } else if (isBankAccount) {
        auditDetails['Bank Account Tokens'] = sourceTokens.reduce((acc, token, index) => {
          acc[`${index + 1}`] = token;
          return acc;
        }, {} as Record<string, string>);
      }

      generateAudit(typhonDomain, auditMessage, auditDetails);

      setNote('');
      setFiles([]);
      setTotalBytes(0);
      setError(null);
    },
    onError: (err: any) => {
      const errorMessage = err?.responseError?.message || err?.message || 'Failed to upload evidence. Please try again.';
      setError(errorMessage);
    },
  });

  const attachFiles = (fileList: File[]) => {
    if (!fileList || fileList.length === 0) return;

    // Validate max documents count
    if (files.length + fileList.length > MAX_DOCUMENTS) {
      setError(`You can upload up to ${MAX_DOCUMENTS} documents at a time.`);
      return;
    }

    // Validate file types
    const invalidFiles = Array.from(fileList).filter(file => {
      const hasValidType = ALLOWED_FILE_TYPES.includes(file.type);
      const hasValidExtension = ALLOWED_EXTENSIONS.some(
        ext => file.name.toLowerCase().endsWith(ext.toLowerCase()),
      );
      return !hasValidType && !hasValidExtension;
    });

    if (invalidFiles.length > 0) {
      setError('Invalid file type. Please upload files in JPG, PNG, PDF, HEIF, HEIC, or MSG format.');
      return;
    }

    // Validate total file size
    const newBytes = fileList.reduce((sum, f) => sum + f.size, 0);
    const nextTotal = totalBytes + newBytes;
    if (nextTotal > MAX_TOTAL_BYTES) {
      setError('Your files are too large to upload. Please make sure the total size is 50MB or less.');
      return;
    }

    setError(null);
    setFiles(prev => [...prev, ...fileList]);
    setTotalBytes(nextTotal);
  };

  const handleCloseModal = () => {
    setFiles([]);
    setTotalBytes(0);
    setNote('');
    setError(null);
    closeModal();
  };

  const handleAddEvidence = async () => {
    if (!contextToken || sourceTokens.length === 0) {
      setError('Missing context or source token');
      return;
    }
    if (note.trim() || files.length > 0) {
      const payload = {
        evidence_sources: sourceTokens.map(token => ({
          evidence_source_token: token,
          evidence_source_type: sourceType,
        })),
        notes: note.trim() ? [{ text: note }] : [],
        documents: files,
        context_token: contextToken,
      };

      await addEvidenceMutation.mutateAsync(payload);
    }
  };

  const attachmentsSlot = attachments.length > 0 ? (
    <CommentBoxAttachments
      items={attachments}
      onRemove={item => {
        const attachmentIndex = attachments.findIndex(a => a.id === item);
        if (attachmentIndex !== -1) {
          const { size } = files[attachmentIndex];

          setFiles(prev => prev.filter((_, idx) => idx !== attachmentIndex));
          setTotalBytes(prev => {
            const next = Math.max(0, prev - size);
            if (next <= MAX_TOTAL_BYTES) setError(null);
            return next;
          });
        }
      }}
    />
  ) : null;
  const actionsSlot = (
    <CommentBoxActions
      leftActions={[
        <AttachButton
          key="attach-button"
          onClick={() => fileInputRef.current?.click()}
        />,
      ]}
      rightActions={[
        <SubmitButton
          key="submit-button"
          onClick={handleAddEvidence}
          disabled={files.length === 0 && note.length === 0}
          loading={addEvidenceMutation.isLoading}
        />,
      ]}
    />
  );

  // Determine modal header based on permissions
  const getModalHeader = () => {
    if (canCreateEvidence && canReadEvidence) {
      return `${sourceType} Evidence`;
    }
    if (canCreateEvidence) {
      return `Add ${sourceType} Evidence`;
    }
    if (canReadEvidence) {
      return `View ${sourceType} Evidence`;
    }
    return `${sourceType} Evidence`;
  };

  return (
    <div>
      <Modal
        header={getModalHeader()}
        isOpen={isOpen}
        onClose={handleCloseModal}
        contentStyle={{ overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {sourceComponent}
          {canCreateEvidence && (
            <>
              <input
                type="file"
                multiple
                accept={ALLOWED_EXTENSIONS.join(',')}
                ref={fileInputRef}
                style={{
                  display: 'none',
                }}
                onChange={e => {
                  const selectedFiles = Array.from(e.target.files ?? []);
                  if (selectedFiles.length) attachFiles(selectedFiles);
                  // allow re-selecting the same file(s)
                  e.currentTarget.value = '';
                }}
              />
              <CommentBox
                placeholder="Add a note or attachments"
                value={note}
                onChange={e => setNote(e.target.value)}
                attachmentsSlot={attachmentsSlot}
                actionsSlot={actionsSlot}
                onDropFile={attachFiles}
                error={error}
                heightBeforeScrolling={10}
              />
            </>
          )}
          {canReadEvidence && evidenceData.events.length > 0 && (
            <>
              <Separator />
              <Box direction="column" gap={2}>
                {evidenceData.events.map(event => (
                  <Box
                    key={event.evidence_event_token}
                    direction="column"
                    gap={2}
                    style={{
                      backgroundColor: 'var(--cl-gray-200)',
                      padding: '12px 16px',
                      borderRadius: 'var(--cl-radius-3)',
                    }}
                  >
                    <Box
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <div
                        style={{
                          fontSize: 'var(--cl-font-size-2)',
                          color: 'var(--cl-text-secondary)',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 'var(--cl-space-1)',
                        }}
                      >
                        <Avatar
                          name={[event.created_by?.first_name, event.created_by?.last_name]
                            .filter(Boolean)
                            .join(' ')}
                          role={AvatarRole.User}
                          size={AvatarSize.Small}
                        />
                        {getEvidenceActionDescription(event.notes, event.documents)}
                      </div>
                      <Text
                        size={1}
                        style={{
                          color: 'var(--cl-text-tertiary)',
                        }}
                      >
                        {new Date(event.created_at).toLocaleString()}
                      </Text>
                    </Box>
                    {Array.isArray(event?.notes) && event.notes.length > 0 && (
                      <Box direction="column" alignItems="flex-start" style={{ marginTop: '8px' }}>
                        {event.notes.map(noteItem => (
                          <Text
                            key={noteItem.note_id}
                            size={2}
                            lineHeight={4}
                            style={{ color: 'var(--cl-text-primary)', textAlign: 'left' }}
                          >
                            {noteItem.text}
                          </Text>
                        ))}
                      </Box>
                    )}
                    {Array.isArray(event?.documents) && event.documents.length > 0 && (
                      <Box
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-end"
                        gap={2}
                      >
                        <Box
                          direction="row"
                          justifyContent="flex-start"
                          gap={1}
                          wrap="wrap"
                          style={{ flex: 1 }}
                        >
                          {event.documents.map(doc => {
                            const fileLabel = `${doc.name}.${doc.extension}`;
                            return (
                              <Box
                                key={doc.document_token}
                                justifyContent="flex-start"
                                style={{ minWidth: 'max-content', maxWidth: 'max-content' }}
                                gap={1}
                              >
                                <Icon
                                  icon={IconName.Paperclip}
                                  size="small"
                                  style={{
                                    backgroundColor: 'var(--cl-gray-400)',
                                    borderRadius: 'var(--cl-radius-1)',
                                  }}
                                />
                                {canDownloadDocuments ? (
                                  <StyledDownloadLink
                                    href="#"
                                    target="_blank"
                                    className="secondary inline"
                                    download={`${fileLabel}`}
                                    rel="noreferrer"
                                    onClick={e => {
                                      e.preventDefault();
                                      downloadDocuments(doc.document_token, fileLabel);
                                    }}
                                  >
                                    {fileLabel}
                                  </StyledDownloadLink>
                                ) : (
                                  <StyledDownloadLink as="span" className="secondary inline">
                                    {fileLabel}
                                  </StyledDownloadLink>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                        {canDownloadDocuments && event.documents.length > 1 && (
                          <Tooltip content="Download all attachments">
                            <Button
                              data-testid="download-all-documents-button"
                              icon={IconName.Download}
                              variant={ButtonVariant.tertiary}
                              size={ButtonSize.small}
                              style={{ flexShrink: 0 }}
                              onClick={() => {
                                const documentTokens = event.documents.map(
                                  d => d.document_token,
                                );
                                downloadDocuments(documentTokens);
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
